import axios from 'axios'
import { REHAB_GUIDANCE_SYSTEM_TYPE } from '@/constants/systemConfig'

const SYSTEM_TYPE = REHAB_GUIDANCE_SYSTEM_TYPE

/**
 * VipDao
 * - 封装 VIP 相关请求
 */
const VipDao = {
  async redeemVipCode ({ merchantCode, exchangeCode, userId, phone }) {
    const url = '/vip/redeem-code/redeem'
    try {
      const { data: resp } = await axios.post(url, {
        merchantCode,
        exchangeCode,
        userId,
        phone
      })
      if (!resp || resp.code !== 200) {
        throw new Error(resp?.message || '兑换失败')
      }
      return resp.data
    } catch (e) {
      console.error('兑换VIP失败:', e)
      throw e
    }
  },
  /**
   * 判断用户是否为有效 VIP（启用、未过期、systemType 匹配）
   * @param {string} userId
   * @returns {Promise<boolean>} 是否有效 VIP
   */
  async isActiveVip (userId) {
    if (!userId) return false
    const url = '/vip/list'
    try {
      const { data: resp } = await axios.get(url, {
        params: {
          page: 1,
          size: 1,
          systemType: SYSTEM_TYPE,
          status: 1,
          userId
        }
      })
      if (!resp || resp.code !== 200 || !resp.data) return false
      const pageData = resp.data
      const list = Array.isArray(pageData.content) ? pageData.content : []
      if (!list.length) return false
      // 校验时间有效性（若后端已过滤可直接返回 true）
      const now = Date.now()
      const first = list[0]
      const start = first.startTime ? new Date(first.startTime).getTime() : 0
      const end = first.endTime ? new Date(first.endTime).getTime() : 0
      if (start && now < start) return false
      if (end && now > end) return false
      return true
    } catch (e) {
      return false
    }
  },

  /**
   * 获取用户VIP详细信息（包含康复指导次数）
   * @param {string} userId
   * @returns {Promise<Object|null>} VIP详细信息，包含totalServiceCount和usedServiceCount
   */
  async getUserVipInfo (userId) {
    if (!userId) return null
    const url = '/vip/list'
    try {
      const { data: resp } = await axios.get(url, {
        params: {
          page: 1,
          size: 1,
          systemType: SYSTEM_TYPE,
          status: 1,
          userId
        }
      })
      if (!resp || resp.code !== 200 || !resp.data) return null
      const pageData = resp.data
      const list = Array.isArray(pageData.content) ? pageData.content : []
      if (!list.length) return null
      
      const vipInfo = list[0]
      
      // 校验时间有效性
      const now = Date.now()
      // 处理时间戳（兼容秒和毫秒）
      const normalizeTimestamp = (ts) => {
        if (!ts) return 0
        const num = Number(ts)
        // 如果小于1e12，认为是秒，需要转换为毫秒
        return num < 1e12 ? num * 1000 : num
      }
      
      const start = normalizeTimestamp(vipInfo.startTime)
      const end = normalizeTimestamp(vipInfo.endTime)
      
      console.log('VIP时间校验:', {
        now: new Date(now).toLocaleString(),
        start: start ? new Date(start).toLocaleString() : '无',
        end: end ? new Date(end).toLocaleString() : '无',
        startTimeRaw: vipInfo.startTime,
        endTimeRaw: vipInfo.endTime
      })
      
      // 只校验结束时间，如果结束时间已过，返回null
      // 开始时间未到也允许显示（服务未开始但已购买）
      if (end && now > end) {
        console.log('VIP已过期：结束时间已过')
        return null
      }
      
      // 处理康复指导次数字段，确保有默认值
      return {
        ...vipInfo,
        totalServiceCount: vipInfo.totalServiceCount || 0,
        usedServiceCount: vipInfo.usedServiceCount || 0,
        remainingServiceCount: Math.max(0, (vipInfo.totalServiceCount || 0) - (vipInfo.usedServiceCount || 0))
      }
    } catch (e) {
      console.error('获取用户VIP信息失败:', e)
      return null
    }
  },

  /**
   * 获取VIP套餐列表
   * @returns {Promise<Array>} VIP套餐列表
   */
  async getVipPlans() {
    const url = '/vip/plans'
    try {
      const { data: resp } = await axios.get(url, {
        params: {
          systemType: SYSTEM_TYPE,
          status: 1
        }
      })
      if (!resp || resp.code !== 200 || !resp.data) return []
      return Array.isArray(resp.data) ? resp.data : []
    } catch (e) {
      console.error('获取VIP套餐列表失败:', e)
      return []
    }
  },

  /**
   * 创建VIP订单
   * @param {string} userId 用户ID
   * @param {number} planId 套餐ID
   * @returns {Promise<Object>} 订单信息
   */
  async createVipOrder(userId, planId) {
    const url = '/vip/order'
    try {
      const { data: resp } = await axios.post(url, {
        userId,
        planId,
        systemType: SYSTEM_TYPE
      })
      if (!resp || resp.code !== 200) {
        throw new Error(resp?.message || '创建订单失败')
      }
      return resp.data
    } catch (e) {
      console.error('创建VIP订单失败:', e)
      throw e
    }
  },

  /**
   * 查询订单状态
   * @param {string} orderId 订单ID
   * @returns {Promise<Object>} 订单状态
   */
  async getOrderStatus(orderId) {
    const url = `/vip/order/${orderId}`
    try {
      const { data: resp } = await axios.get(url)
      if (!resp || resp.code !== 200) {
        throw new Error(resp?.message || '查询订单状态失败')
      }
      return resp.data
    } catch (e) {
      console.error('查询订单状态失败:', e)
      throw e
    }
  },

  /**
   * 获取用户VIP权益信息
   * @param {string} userId 用户ID
   * @returns {Promise<Object>} VIP权益信息，包含康复指导次数统计
   */
  async getUserVipEntitlement(userId) {
    const url = '/vip/entitlement'
    try {
      const { data: resp } = await axios.get(url, {
        params: {
          userId,
          systemType: SYSTEM_TYPE
        }
      })
      if (!resp || resp.code !== 200) {
        throw new Error(resp?.message || '获取VIP权益失败')
      }
      
      // 处理康复指导次数字段，确保有默认值
      const entitlementData = resp.data || {}
      return {
        ...entitlementData,
        totalServiceCount: entitlementData.totalServiceCount || 0,
        usedServiceCount: entitlementData.usedServiceCount || 0,
        remainingServiceCount: Math.max(0, (entitlementData.totalServiceCount || 0) - (entitlementData.usedServiceCount || 0))
      }
    } catch (e) {
      console.error('获取VIP权益失败:', e)
      throw e
    }
  },

  /**
   * 获取康复指导服务统计
   * @param {string} userId 用户ID
   * @returns {Promise<Object>} 康复指导服务次数统计
   */
  async getServiceStats(userId) {
    try {
      // 优先从VIP详细信息获取
      const vipInfo = await this.getUserVipInfo(userId)
      if (vipInfo) {
        return {
          total: vipInfo.totalServiceCount || 0,
          used: vipInfo.usedServiceCount || 0,
          remaining: vipInfo.remainingServiceCount || 0
        }
      }
      
      // 回退到权益接口
      const entitlement = await this.getUserVipEntitlement(userId)
      return {
        total: entitlement.totalServiceCount || 0,
        used: entitlement.usedServiceCount || 0,
        remaining: entitlement.remainingServiceCount || 0
      }
    } catch (e) {
      console.error('获取康复指导服务统计失败:', e)
      return {
        total: 0,
        used: 0,
        remaining: 0
      }
    }
  }
}

export default VipDao


